import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Landmark, Percent, Wallet, TrendingDown, ChevronRight, Trash2,
  ArrowUpRight, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PARTNER_TYPES = [
  { id: "bank", label: "Bank" },
  { id: "individual", label: "Individual" },
  { id: "government", label: "Government" },
  { id: "other", label: "Other" },
];

const STATUS_META = {
  active: { label: "Active", cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  paid_off: { label: "Paid off", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  overdue: { label: "Overdue", cls: "bg-red-50 text-red-800 ring-red-200" },
  written_off: { label: "Written off", cls: "bg-slate-100 text-slate-700 ring-slate-200" },
};

const emptyLoan = {
  loan_number: "",
  partner_name: "",
  partner_type: "bank",
  partner_contact: "",
  principal: "",
  currency: "USD",
  disbursement_date: new Date().toISOString().slice(0, 10),
  interest_rate_pct: "",
  term_months: "",
  maturity_date: "",
  notes: "",
};

const emptyRepayment = {
  payment_date: new Date().toISOString().slice(0, 10),
  principal_portion: "",
  interest_portion: "",
  payment_method: "bank_transfer",
  notes: "",
  auto_post_expense: true,
};

export default function Loans() {
  const qc = useQueryClient();
  const [loanFormOpen, setLoanFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLoan);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detailOpen, setDetailOpen] = useState(null); // loanId or null
  const [repayOpen, setRepayOpen] = useState(false);
  const [repayForm, setRepayForm] = useState(emptyRepayment);

  const { data: loans = [], isLoading } = useQuery({
    queryKey: ["loans"],
    queryFn: () => api.listLoans(),
  });
  const { data: stats } = useQuery({
    queryKey: ["loans-stats"],
    queryFn: () => api.loansStats(),
  });

  const activeLoan = useMemo(
    () => loans.find((l) => l.id === detailOpen) || null,
    [loans, detailOpen]
  );

  const createMut = useMutation({
    mutationFn: (body) =>
      editing ? api.updateLoan(editing.id, body) : api.createLoan(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["loans-stats"] });
      setLoanFormOpen(false);
      setEditing(null);
      setForm(emptyLoan);
      toast.success(editing ? "Loan updated" : "Loan recorded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteLoan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["loans-stats"] });
      setConfirmDelete(null);
      setDetailOpen(null);
      toast.success("Loan deleted");
    },
  });

  const addRepayMut = useMutation({
    mutationFn: ({ id, body }) => api.addLoanRepayment(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["loans-stats"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      setRepayOpen(false);
      setRepayForm(emptyRepayment);
      toast.success("Repayment recorded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  const deleteRepayMut = useMutation({
    mutationFn: ({ loanId, repId }) => api.deleteLoanRepayment(loanId, repId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["loans"] });
      qc.invalidateQueries({ queryKey: ["loans-stats"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      toast.success("Repayment removed");
    },
  });

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyLoan, disbursement_date: new Date().toISOString().slice(0, 10) });
    setLoanFormOpen(true);
  };
  const openEdit = (loan) => {
    setEditing(loan);
    setForm({
      loan_number: loan.loan_number,
      partner_name: loan.partner_name,
      partner_type: loan.partner_type,
      partner_contact: loan.partner_contact || "",
      principal: loan.principal,
      currency: loan.currency || "USD",
      disbursement_date: loan.disbursement_date,
      interest_rate_pct: loan.interest_rate_pct,
      term_months: loan.term_months || "",
      maturity_date: loan.maturity_date || "",
      notes: loan.notes || "",
    });
    setLoanFormOpen(true);
  };

  const submit = () => {
    if (!form.partner_name.trim() || !Number(form.principal)) {
      toast.error("Partner and principal are required");
      return;
    }
    createMut.mutate({
      ...form,
      principal: Number(form.principal),
      interest_rate_pct: Number(form.interest_rate_pct || 0),
      term_months: Number(form.term_months || 0),
    });
  };

  const submitRepayment = () => {
    const p = Number(repayForm.principal_portion || 0);
    const i = Number(repayForm.interest_portion || 0);
    if (p + i <= 0) {
      toast.error("Enter a principal and/or interest amount");
      return;
    }
    addRepayMut.mutate({
      id: activeLoan.id,
      body: { ...repayForm, principal_portion: p, interest_portion: i },
    });
  };

  return (
    <div data-testid="loans-page">
      <PageHeader
        overline="finance"
        title="Financing"
        actions={
          <Button data-testid="loan-new-btn" onClick={openNew} className="bg-primary hover:bg-primary/90">
            <Plus className="size-4 mr-1.5" /> New loan
          </Button>
        }
      />

      {/* KPI ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <Kpi testid="loan-kpi-active" label="Active loans" icon={Landmark} tone="slate"
             value={stats?.active_loans ?? "—"} secondary={`of ${stats?.total_loans ?? 0} total`} />
        <Kpi testid="loan-kpi-outstanding" label="Total outstanding" icon={TrendingDown} tone="red"
             value={fmtMoney(stats?.total_outstanding ?? 0)} secondary="USD" />
        <Kpi testid="loan-kpi-principal" label="Total principal" icon={Wallet} tone="slate"
             value={fmtMoney(stats?.total_principal ?? 0)} secondary="USD" />
        <Kpi testid="loan-kpi-interest" label={`Interest YTD ${stats?.year || ""}`} icon={Percent} tone="amber"
             value={fmtMoney(stats?.interest_paid_ytd ?? 0)} secondary="Auto-posted to Expenses" />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : loans.length === 0 ? (
        <EmptyState title="No loans yet" hint="Click 'New loan' to record a bank loan or partner financing." />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Loan #</TableHead>
                <TableHead className="overline">Partner</TableHead>
                <TableHead className="overline">Disbursed</TableHead>
                <TableHead className="overline text-right">Principal</TableHead>
                <TableHead className="overline text-right">Rate</TableHead>
                <TableHead className="overline text-right">Outstanding</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline w-8">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loans.map((l) => {
                const s = STATUS_META[l.status] || STATUS_META.active;
                return (
                  <TableRow key={l.id} data-testid={`loan-row-${l.id}`} className="cursor-pointer hover:bg-primary/5" onClick={() => setDetailOpen(l.id)}>
                    <TableCell className="mono text-xs font-semibold text-primary">{l.loan_number}</TableCell>
                    <TableCell>
                      <div className="font-medium">{l.partner_name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase">{l.partner_type}</div>
                    </TableCell>
                    <TableCell className="text-xs mono">{fmtDate(l.disbursement_date)}</TableCell>
                    <TableCell className="text-right mono">{fmtMoney(l.principal)} {l.currency}</TableCell>
                    <TableCell className="text-right mono text-amber-800">{Number(l.interest_rate_pct).toFixed(2)}%</TableCell>
                    <TableCell className={`text-right mono font-semibold ${Number(l.outstanding_balance) <= 0.005 ? "text-emerald-700" : "text-red-700"}`}>
                      {fmtMoney(l.outstanding_balance)} {l.currency}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${s.cls}`}>
                        {s.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <ChevronRight className="size-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* ────── Create / edit loan dialog ────── */}
      <Dialog open={loanFormOpen} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyLoan); } setLoanFormOpen(v); }}>
        <DialogContent data-testid="loan-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit loan" : "Record a new loan"}</DialogTitle>
            <DialogDescription className="text-xs">
              Money loaded from a bank or partner. Track principal, interest, and repayments over time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Partner *</Label>
                <Input data-testid="loan-form-partner" value={form.partner_name}
                  onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                  placeholder="e.g. BNU Bank" />
              </div>
              <div>
                <Label className="overline">Type</Label>
                <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                  <SelectTrigger data-testid="loan-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="overline">Principal *</Label>
                <Input data-testid="loan-form-principal" type="number" min="0" step="0.01"
                  className="font-mono"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  placeholder="0.00" />
              </div>
              <div>
                <Label className="overline">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="loan-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="IDR">IDR</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="overline">Interest % *</Label>
                <Input data-testid="loan-form-rate" type="number" min="0" max="100" step="0.01"
                  className="font-mono"
                  value={form.interest_rate_pct}
                  onChange={(e) => setForm({ ...form, interest_rate_pct: e.target.value })}
                  placeholder="8.50" />
              </div>
              <div>
                <Label className="overline">Term (months)</Label>
                <Input data-testid="loan-form-term" type="number" min="0"
                  value={form.term_months}
                  onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                  placeholder="36" />
              </div>
              <div>
                <Label className="overline">Disbursed *</Label>
                <Input data-testid="loan-form-disbursed" type="date"
                  value={form.disbursement_date}
                  onChange={(e) => setForm({ ...form, disbursement_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Contact person</Label>
                <Input data-testid="loan-form-contact" value={form.partner_contact}
                  onChange={(e) => setForm({ ...form, partner_contact: e.target.value })}
                  placeholder="e.g. Loan officer name / phone" />
              </div>
              <div>
                <Label className="overline">Maturity date</Label>
                <Input data-testid="loan-form-maturity" type="date"
                  value={form.maturity_date}
                  onChange={(e) => setForm({ ...form, maturity_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="overline">Notes</Label>
              <Textarea data-testid="loan-form-notes" rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanFormOpen(false)} data-testid="loan-form-cancel">Cancel</Button>
            <Button data-testid="loan-form-save" disabled={createMut.isPending}
              className="bg-primary hover:bg-primary/90" onClick={submit}>
              {createMut.isPending ? "Saving…" : editing ? "Save changes" : "Record loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ────── Loan detail dialog ────── */}
      <Dialog open={!!activeLoan} onOpenChange={(v) => !v && setDetailOpen(null)}>
        <DialogContent data-testid="loan-detail-dialog" className="max-w-2xl">
          {activeLoan && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="mono text-primary">{activeLoan.loan_number}</span>
                  <span className="text-slate-400">·</span>
                  <span>{activeLoan.partner_name}</span>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Disbursed {fmtDate(activeLoan.disbursement_date)} · {Number(activeLoan.interest_rate_pct).toFixed(2)}% p.a.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <MiniStat label="Principal" value={fmtMoney(activeLoan.principal)} currency={activeLoan.currency} tone="slate" />
                <MiniStat label="Repaid" value={fmtMoney(activeLoan.principal_paid)} currency={activeLoan.currency} tone="emerald" />
                <MiniStat label="Outstanding" value={fmtMoney(activeLoan.outstanding_balance)} currency={activeLoan.currency}
                  tone={Number(activeLoan.outstanding_balance) <= 0.005 ? "emerald" : "red"} />
              </div>

              <div className="flex items-center justify-between mb-2">
                <h3 className="overline">Repayments</h3>
                <div className="flex gap-2">
                  <Button data-testid="loan-repay-btn" size="sm"
                    onClick={() => { setRepayForm(emptyRepayment); setRepayOpen(true); }}
                    disabled={Number(activeLoan.outstanding_balance) <= 0.005}
                    className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="size-3.5 mr-1" /> Record repayment
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(activeLoan)} data-testid="loan-edit-btn">
                    Edit
                  </Button>
                  <Button size="sm" variant="outline"
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    onClick={() => setConfirmDelete(activeLoan.id)}
                    data-testid="loan-delete-btn">
                    Delete
                  </Button>
                </div>
              </div>

              <div className="border border-border rounded-md overflow-hidden">
                {(activeLoan.repayments || []).length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">No repayments yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="overline">Date</TableHead>
                        <TableHead className="overline text-right">Principal</TableHead>
                        <TableHead className="overline text-right">Interest</TableHead>
                        <TableHead className="overline text-right">Total</TableHead>
                        <TableHead className="overline">Method</TableHead>
                        <TableHead className="overline w-8">&nbsp;</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeLoan.repayments.map((r) => (
                        <TableRow key={r.id} data-testid={`repayment-${r.id}`}>
                          <TableCell className="text-xs mono">{fmtDate(r.payment_date)}</TableCell>
                          <TableCell className="text-right mono text-slate-800">{fmtMoney(r.principal_portion)}</TableCell>
                          <TableCell className="text-right mono text-amber-800">
                            {fmtMoney(r.interest_portion)}
                            {r.expense_id && (
                              <ArrowUpRight className="inline-block size-3 ml-1 text-emerald-600" data-testid={`repayment-exp-${r.id}`} />
                            )}
                          </TableCell>
                          <TableCell className="text-right mono font-semibold">{fmtMoney(r.amount)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{(r.payment_method || "").replace("_", " ")}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600"
                              data-testid={`repayment-delete-${r.id}`}
                              onClick={() => {
                                if (window.confirm("Delete this repayment? The linked interest-expense entry will also be removed.")) {
                                  deleteRepayMut.mutate({ loanId: activeLoan.id, repId: r.id });
                                }
                              }}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {activeLoan.notes && (
                <div className="mt-3 text-xs text-muted-foreground bg-slate-50 rounded p-2">
                  <span className="overline mr-1">Notes:</span> {activeLoan.notes}
                </div>
              )}

              {Number(activeLoan.outstanding_balance) <= 0.005 && (
                <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-3 py-2 flex items-center gap-2">
                  <CheckCircle2 className="size-4" /> This loan has been fully repaid.
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ────── Repayment dialog ────── */}
      <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
        <DialogContent data-testid="repayment-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record a repayment</DialogTitle>
            <DialogDescription className="text-xs">
              Split the payment into principal (reduces balance) and interest
              (auto-posts as an "Interest" overhead expense).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Payment date *</Label>
              <Input data-testid="repayment-form-date" type="date"
                value={repayForm.payment_date}
                onChange={(e) => setRepayForm({ ...repayForm, payment_date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Principal portion</Label>
                <Input data-testid="repayment-form-principal" type="number" min="0" step="0.01"
                  className="font-mono"
                  value={repayForm.principal_portion}
                  onChange={(e) => setRepayForm({ ...repayForm, principal_portion: e.target.value })} />
              </div>
              <div>
                <Label className="overline">Interest portion</Label>
                <Input data-testid="repayment-form-interest" type="number" min="0" step="0.01"
                  className="font-mono"
                  value={repayForm.interest_portion}
                  onChange={(e) => setRepayForm({ ...repayForm, interest_portion: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="overline">Payment method</Label>
              <Select value={repayForm.payment_method}
                onValueChange={(v) => setRepayForm({ ...repayForm, payment_method: v })}>
                <SelectTrigger data-testid="repayment-form-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="credit_card">Credit card</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Notes</Label>
              <Textarea data-testid="repayment-form-notes" rows={2}
                value={repayForm.notes}
                onChange={(e) => setRepayForm({ ...repayForm, notes: e.target.value })} />
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                data-testid="repayment-form-autopost"
                type="checkbox"
                className="mt-0.5 size-4 accent-emerald-600"
                checked={repayForm.auto_post_expense}
                onChange={(e) => setRepayForm({ ...repayForm, auto_post_expense: e.target.checked })} />
              <span>Auto-post interest to Expenses as an "Interest" overhead entry.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepayOpen(false)} data-testid="repayment-form-cancel">Cancel</Button>
            <Button data-testid="repayment-form-save" disabled={addRepayMut.isPending}
              className="bg-emerald-600 hover:bg-emerald-700" onClick={submitRepayment}>
              {addRepayMut.isPending ? "Saving…" : "Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this loan?</AlertDialogTitle>
            <AlertDialogDescription>
              All repayments and linked interest-expense entries will also be removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="loan-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="loan-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDelete)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Kpi({ testid, label, icon: Icon, value, secondary, tone = "slate" }) {
  const toneMap = {
    slate: "bg-white border-slate-200",
    red: "bg-red-50 border-red-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    red: "text-red-700 bg-red-100",
    emerald: "text-emerald-700 bg-emerald-100",
    amber: "text-amber-800 bg-amber-100",
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-3 flex items-center justify-between ${toneMap[tone]}`}>
      <div>
        <p className="overline">{label}</p>
        <p className="text-xl font-bold font-mono mt-1">{value}</p>
        {secondary && <p className="text-[10px] text-muted-foreground">{secondary}</p>}
      </div>
      <span className={`size-9 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-4" />
      </span>
    </div>
  );
}

function MiniStat({ label, value, currency, tone = "slate" }) {
  const cls = {
    slate: "text-slate-900",
    emerald: "text-emerald-800 bg-emerald-50",
    red: "text-red-800 bg-red-50",
  }[tone];
  return (
    <div className={`rounded-md border border-border p-2 ${tone === "slate" ? "bg-white" : ""} ${cls}`}>
      <div className="overline">{label}</div>
      <div className="text-base font-bold font-mono mt-0.5">
        {value} <span className="text-[10px] text-muted-foreground">{currency}</span>
      </div>
    </div>
  );
}
